// Push notifications (wave 3, W2_Portal_Tables look): three tabs — Sent,
// Scheduled (with how many are still waiting) and Delivery — in the theme's
// tabs, each list in the modern table (Addis Ababa 12-hour dates, status as
// an icon and a word), New notification top right, Delete after a question
// that names the notification.
//
// Data, exactly as before: both lists are fetched once on arrival
// (fetchNotifications, fetchScheduledNotifications); the Delivery tab mounts
// only when opened (its campaign listener attaches then and detaches when
// the tab closes). Sent: Delete = editNotifications(row, 'Delete').
// Scheduled: Edit (only while still 'scheduled') opens the form with the
// record; Delete = deleteScheduledNotification(row). Adding, editing and
// deleting need canManagePushNotifications (admins and the
// canManageNotifications capability), as before. A deleted row disappears at
// once (the store keeps the list it fetched).
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { tokens as c, toneChipSx } from '../theme';
import { confirmDialog, showToast } from '../components/feedback';
import { DataTable, PageHeader, StatusChip, calmCaps, formatNumber, usePageState } from '../components/modern';
import { canManagePushNotifications } from '../common/notificationSecurity';
import CampaignDelivery from './CampaignDelivery';

const EMPTY = '—';

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  // Admins by role, plus any staff member an admin has granted the
  // canManageNotifications capability (e.g. IT support).
  const canManage = canManagePushNotifications(auth.profile);
  const { editNotifications, fetchNotifications, fetchScheduledNotifications, deleteScheduledNotification } = api;
  const notificationdata = useSelector((state) => state.notificationdata);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [tabValue, setTabValue] = usePageState('notifications', 'tab', 0);
  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('notifications_sent', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  // Rows deleted here (the store keeps the list it fetched).
  const [removed, setRemoved] = useState([]);

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // Land on Delivery straight after a send, so the operator sees the campaign
  // moving instead of wondering whether anything happened.
  useEffect(() => {
    if (state?.openDelivery) setTabValue(2);
  }, [state]);

  // Both tabs' data is fetched here (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchScheduledNotifications());
  }, [dispatch, fetchNotifications, fetchScheduledNotifications]);

  const data = useMemo(
    () => (notificationdata.notifications ? notificationdata.notifications.filter((n) => !removed.includes(`sent:${n.id}`)) : []),
    [notificationdata.notifications, removed]
  );
  const scheduledData = useMemo(
    () => (notificationdata.scheduled ? notificationdata.scheduled.filter((n) => !removed.includes(`scheduled:${n.id}`)) : []),
    [notificationdata.scheduled, removed]
  );
  const pendingCount = scheduledData.filter((n) => n.status === 'scheduled').length;

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const loading = !!notificationdata.loading;
  const deviceLookup = { All: t('all'), ANDROID: t('android'), IOS: t('ios') };
  const userLookup = { customer: t('customer'), driver: t('driver') };

  const addNotification = () => navigate('/notifications/addnotifications', { state: { pageNo: page } });

  const askDelete = (row) =>
    confirmDialog({
      title: L('notification_delete_title', 'Delete “{{title}}”?', { title: row.title || EMPTY }),
      body: L('notification_delete_body', 'It is removed from this list. Messages already delivered stay on the phones.'),
      confirmLabel: L('delete', 'Delete'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
      destructive: true,
    });

  const deleteSent = async (row) => {
    if (!(await askDelete(row))) return;
    dispatch(editNotifications(row, 'Delete'));
    setRemoved((prev) => prev.concat([`sent:${row.id}`]));
    showToast.success(L('notification_deleted', 'Notification deleted'));
  };

  const deleteScheduled = async (row) => {
    if (!(await askDelete(row))) return;
    dispatch(deleteScheduledNotification(row));
    setRemoved((prev) => prev.concat([`scheduled:${row.id}`]));
    showToast.success(L('notification_deleted', 'Notification deleted'));
  };

  const editScheduled = (rowData) => {
    navigate('/notifications/addnotifications', { state: { editData: rowData, pageNo: page } });
  };

  // ── Columns ──────────────────────────────────────────────────────────
  const textCol = (id, title) => ({ id, title, field: id, minWidth: id === 'body' ? 240 : 160, cellSx: { whiteSpace: 'pre-wrap' } });
  const smsCol = { id: 'sendSMS', title: t('sms', { defaultValue: 'SMS' }), field: 'sendSMS', type: 'boolean' };

  const sentColumns = [
    { id: 'createdAt', title: t('createdAt'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    { id: 'devicetype', title: t('device_type'), field: 'devicetype', lookup: deviceLookup },
    { id: 'usertype', title: t('user_type'), field: 'usertype', lookup: userLookup },
    textCol('title', t('title')),
    textCol('body', t('body')),
    smsCol,
  ];

  // The processor is a dispatcher now: it hands a due notification to a
  // delivery campaign and the campaign writes the final status back. So
  // `dispatched` means "queued and being delivered".
  const scheduleStatus = (row) => {
    const s = row.status || 'scheduled';
    return (
      {
        scheduled: { tone: 'info', icon: <ScheduleRoundedIcon />, label: t('scheduled') },
        processing: { tone: 'warning', icon: <HourglassEmptyRoundedIcon />, label: calmCaps(t('processing')) },
        dispatching: { tone: 'warning', icon: <HourglassEmptyRoundedIcon />, label: t('queueing', { defaultValue: 'Queueing' }) },
        dispatched: { tone: 'warning', icon: <HourglassEmptyRoundedIcon />, label: t('delivering', { defaultValue: 'Delivering' }) },
        sending: { tone: 'warning', icon: <HourglassEmptyRoundedIcon />, label: t('delivering', { defaultValue: 'Delivering' }) },
        sent: { tone: 'success', icon: <CheckRoundedIcon />, label: t('sent') },
        partial: { tone: 'amber', icon: <ErrorOutlineRoundedIcon />, label: t('partly_delivered', { defaultValue: 'Partly delivered' }) },
        cancelled: { tone: 'neutral', icon: <BlockRoundedIcon />, label: t('stopped', { defaultValue: 'Stopped' }) },
        stalled: { tone: 'error', icon: <ErrorOutlineRoundedIcon />, label: t('stalled', { defaultValue: 'Needs attention' }) },
        failed: { tone: 'error', icon: <ErrorOutlineRoundedIcon />, label: t('failed') },
      }[s] || { tone: 'neutral', label: s }
    );
  };

  // Real delivery numbers, written back by the campaign that owns this
  // occurrence.
  const deliveredCell = (rowData) => {
    const queued = rowData.queued;
    const result = rowData.result;
    if (!result && !queued) return <Box component="span" sx={{ color: c.TEXT_3 }}>{EMPTY}</Box>;
    const push = (result && result.push) || (queued ? { sent: 0, total: queued.push } : null);
    const sms = (result && result.sms) || (queued && queued.sms ? { sent: 0, total: queued.sms } : null);
    return (
      <Box sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {push && push.total > 0 ? (
          <Box>
            {formatNumber(push.sent || 0)} / {formatNumber(push.total)} {t('push', { defaultValue: 'Push' })}
          </Box>
        ) : null}
        {sms && sms.total > 0 ? (
          <Box>
            {formatNumber(sms.sent || 0)} / {formatNumber(sms.total)} {t('sms', { defaultValue: 'SMS' })}
          </Box>
        ) : null}
        {(!push || !push.total) && (!sms || !sms.total) ? (
          <Box component="span" sx={{ color: c.TEXT_3 }}>
            {t('no_recipients', { defaultValue: 'no recipients' })}
          </Box>
        ) : null}
      </Box>
    );
  };

  const recurringLabel = (row) => {
    if (!row.recurring || !row.recurring.frequency) return '';
    const freq = row.recurring.frequency;
    return freq === 'daily' ? t('repeat_daily') : freq === 'weekly' ? t('repeat_weekly') : freq === 'custom' ? t('repeat_custom') : freq;
  };

  const scheduledColumns = [
    { id: 'createdAt', title: t('createdAt'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'status',
      title: t('status'),
      field: 'status',
      type: 'status',
      status: scheduleStatus,
      sortValue: (row) => scheduleStatus(row).label,
      searchValue: (row) => scheduleStatus(row).label,
    },
    { id: 'result', title: t('delivered', { defaultValue: 'Delivered' }), field: 'result', sortable: false, searchable: false, render: deliveredCell },
    { id: 'scheduledAt', title: t('scheduled_time'), field: 'scheduledAt', type: 'datetime' },
    { id: 'devicetype', title: t('device_type'), field: 'devicetype', lookup: deviceLookup },
    { id: 'usertype', title: t('user_type'), field: 'usertype', lookup: userLookup },
    textCol('title', t('title')),
    textCol('body', t('body')),
    smsCol,
    {
      id: 'recurring',
      title: t('recurring'),
      field: 'recurring',
      sortValue: recurringLabel,
      searchValue: recurringLabel,
      render: (row) => {
        const label = recurringLabel(row);
        if (!label) return <Box component="span" sx={{ color: c.TEXT_3 }}>{EMPTY}</Box>;
        return <StatusChip tone="info" icon={<RepeatRoundedIcon />} label={label} />;
      },
    },
  ];

  const sentActions = canManage
    ? [{ key: 'delete', label: L('notification_delete', 'Delete notification'), icon: <DeleteOutlineRoundedIcon />, inline: true, destructive: true, onClick: deleteSent }]
    : [];
  const scheduledActions = canManage
    ? [
        {
          key: 'edit',
          label: t('edit'),
          icon: <EditOutlinedIcon />,
          inline: true,
          disabled: (row) => row.status !== 'scheduled',
          onClick: editScheduled,
        },
        { key: 'delete', label: L('notification_delete', 'Delete notification'), icon: <DeleteOutlineRoundedIcon />, inline: true, destructive: true, onClick: deleteScheduled },
      ]
    : [];

  const countFor = (noun) => ({ shown, total, query, loading: busy }) => {
    if (busy) return L('loading', 'Loading…');
    if (query) return L('table_count_shown', '{{shown}} of {{total}}', { shown, total });
    return noun(total);
  };
  const sentCount = (n) =>
    t('notifications_count', { count: n, defaultValue_one: '{{count}} notification', defaultValue_other: '{{count}} notifications', defaultValue: '{{count}} notifications' });

  const addLabel = calmCaps(t('add_notification'));
  const emptyFor = (icon, title, body) => ({ query }) =>
    query ? null : { icon, title, body, actionLabel: canManage ? addLabel : undefined, onAction: canManage ? addNotification : undefined };

  const sentTitle = t('sent_notifications');
  const scheduledTitle = t('scheduled_notifications');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: addNotification, show: canManage }} />
      <Box>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          aria-label={t('push_notification_title')}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${c.OUTLINE}` }}
        >
          <Tab label={sentTitle} id="notifications-tab-0" aria-controls="notifications-tabpanel-0" />
          <Tab
            id="notifications-tab-1"
            aria-controls="notifications-tabpanel-1"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {scheduledTitle}
                {pendingCount > 0 ? (
                  <Chip
                    component="span"
                    size="small"
                    label={formatNumber(pendingCount)}
                    aria-label={L('notifications_scheduled_waiting', '{{count}} waiting', { count: pendingCount })}
                    sx={{ ...toneChipSx('accent'), height: 22, fontSize: 12, '& .MuiChip-label': { px: 0.75 } }}
                  />
                ) : null}
              </Box>
            }
          />
          {/* Live per-campaign delivery. Mounted lazily: its listener only
              attaches once the tab is opened, so the other tabs cost nothing. */}
          <Tab label={t('delivery', { defaultValue: 'Delivery' })} id="notifications-tab-2" aria-controls="notifications-tabpanel-2" />
        </Tabs>
        <Box role="tabpanel" id={`notifications-tabpanel-${tabValue}`} aria-labelledby={`notifications-tab-${tabValue}`} sx={{ pt: 2.5 }}>
          {tabValue === 0 ? (
            <DataTable
              title={sentTitle}
              rows={data}
              columns={sentColumns}
              loading={loading && !notificationdata.notifications}
              countText={countFor(sentCount)}
              searchPlaceholder={L('notifications_search', 'Search title or text')}
              rowLabel={(row) => row.title}
              actions={sentActions}
              page={page}
              onPageChange={setPage}
              prefsKey="notifications_sent"
              stateKey="notifications_sent"
              emptyState={emptyFor(
                <NotificationsNoneOutlinedIcon />,
                L('notifications_empty', 'No notifications sent yet'),
                L('notifications_empty_body', 'Push notifications sent to customers or drivers appear here.')
              )}
              minWidth={900}
            />
          ) : null}
          {tabValue === 1 ? (
            <DataTable
              title={scheduledTitle}
              rows={scheduledData}
              columns={scheduledColumns}
              loading={loading && !notificationdata.scheduled}
              countText={countFor(sentCount)}
              searchPlaceholder={L('notifications_search', 'Search title or text')}
              rowLabel={(row) => row.title}
              actions={scheduledActions}
              prefsKey="notifications_scheduled"
              stateKey="notifications_scheduled"
              emptyState={emptyFor(
                <EventNoteOutlinedIcon />,
                L('notifications_scheduled_empty', 'No scheduled notifications'),
                L('notifications_scheduled_empty_body', 'Notifications set to go out later, once or on repeat, appear here.')
              )}
              minWidth={1280}
            />
          ) : null}
          {tabValue === 2 ? <CampaignDelivery /> : null}
        </Box>
      </Box>
    </Box>
  );
}
